import { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api-client';
import { noticeParts } from '../../lib/notice-link';
import { formatMoney, poundsToPence } from '../../lib/money';
import { DocLogo } from './DocLogo';

export interface QuotationPreviewLine {
  qty: string;
  description: string;
  pounds: string;
}

const DEFAULTS = {
  companyName: 'Maxiron Ltd',
  companyNumber: '14444817',
  vatNumber: '435737379',
  companyAddress: '3rd Floor 86-90 Paul Street, London, EC2A 4NE',
  officeEmail: 'office@maxiron.com',
};

function dottedDate(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return match ? `${match[3]}.${match[2]}.${match[1]}` : iso;
}

function NoticeText({ value }: { value: string }) {
  const lines = value.replace(/\t/g, ' ').split('\n');
  return (
    <span className="block">
      {lines.map((line, index) => (
        <span key={index} className="block" style={{ marginTop: index ? 6 : 0 }}>
          {line.trim()
            ? noticeParts(line).map((part, partIndex) => part.href
              ? <a key={partIndex} href={part.href} className="underline" style={{ color: '#1155cc' }} target="_blank" rel="noreferrer">{part.text}</a>
              : <span key={partIndex}>{part.text}</span>)
            : '\u00a0'}
        </span>
      ))}
    </span>
  );
}

export function QuotationPreview({
  number,
  customerName,
  title,
  issuedOn,
  currency,
  billToName,
  billToAddress,
  billToEmail,
  contactName,
  contactRole,
  contactEmail,
  contactPhone,
  notice,
  lines,
  vatPence,
  validDays,
  scope,
  paymentTerms,
  delivery,
  acceptance,
}: {
  number?: string;
  customerName: string;
  title: string;
  issuedOn: string;
  currency?: string;
  billToName: string;
  billToAddress: string;
  billToEmail: string;
  contactName: string;
  contactRole: string;
  contactEmail: string;
  contactPhone: string;
  notice: string;
  lines: QuotationPreviewLine[];
  vatPence?: number | null;
  validDays?: number;
  scope?: string;
  paymentTerms?: string;
  delivery?: string;
  acceptance?: string;
}) {
  const [company, setCompany] = useState(DEFAULTS);
  useEffect(() => {
    void apiGet<{ settings?: Record<string, string> }>('/api/settings').then((data) => {
      const s = data.settings || {};
      setCompany({
        companyName: s.companyName || DEFAULTS.companyName,
        companyNumber: s.companyNumber || DEFAULTS.companyNumber,
        vatNumber: s.vatNumber || DEFAULTS.vatNumber,
        companyAddress: s.companyAddress || DEFAULTS.companyAddress,
        officeEmail: s.billingEmail || DEFAULTS.officeEmail,
      });
    }).catch(() => setCompany(DEFAULTS));
  }, []);

  const money = currency || 'GBP';
  const shown = lines.filter((line) => line.description || line.pounds);
  const net = shown.reduce((sum, line) => sum + Math.round((Number(line.qty) || 1) * poundsToPence(line.pounds)), 0);
  const vatKnown = vatPence != null;
  const vat = vatPence ?? 0;
  const total = vatKnown ? net + vat : net;
  const address = billToAddress.split(/\n|,/).map((part) => part.trim()).filter(Boolean);
  const terms: [string, string][] = [
    ['Quotation validity', validDays && validDays > 0 ? `${validDays} days` : ''],
    ['Payment terms', paymentTerms || ''],
    ['Delivery', delivery || ''],
    ['Scope & exclusions', scope || ''],
    ['Terms & conditions', notice || ''],
    ['To proceed', acceptance || ''],
  ].filter((row): row is [string, string] => Boolean(row[1].trim()));

  return (
    <article className="doc-sheet doc-quotation">
      <div className="flex items-start justify-between gap-4">
        <DocLogo />
        <h1 className="text-lg font-bold tracking-wide">QUOTATION</h1>
      </div>
      <div className="mt-3 flex items-start justify-between gap-4">
        <div>
          <p className="text-[11pt] font-bold">{company.companyName}</p>
          <p>Company No.: {company.companyNumber} &nbsp; VAT No.: {company.vatNumber}</p>
          <p>Address: {company.companyAddress}</p>
          <p>Contact: {company.officeEmail}</p>
        </div>
        <div className="text-right">
          <p>QUOTATION NO. {number || 'Draft'}</p>
          <p>DATE: {dottedDate(issuedOn)}</p>
        </div>
      </div>
      <div className="mt-4 flex items-start gap-6">
        <div className="w-1/2">
          <p><span className="font-bold">TO</span> {billToName || customerName}</p>
          {address.map((line) => <p key={line}>{line}</p>)}
          {billToEmail ? <p>e: <a className="underline" href={`mailto:${billToEmail}`}>{billToEmail}</a></p> : null}
        </div>
        <h2 className="w-1/2 text-right text-[11pt] font-bold">{title || 'Quotation'}</h2>
      </div>
      <table className="doc-grid mt-3">
        <thead>
          <tr>{['CONTACT', 'ROLE', 'EMAIL', 'TELEPHONE'].map((label) => <th key={label} className="w-1/4">{label}</th>)}</tr>
        </thead>
        <tbody>
          <tr>
            <td>{contactName}</td>
            <td>{contactRole}</td>
            <td>{contactEmail}</td>
            <td>{contactPhone}</td>
          </tr>
        </tbody>
      </table>
      <table className="doc-grid mt-3">
        <thead>
          <tr>
            <th className="w-12">QTY</th>
            <th>DESCRIPTION</th>
            <th className="right w-28">UNIT PRICE</th>
            <th className="right w-28">LINE TOTAL</th>
          </tr>
        </thead>
        <tbody>
          {(shown.length ? shown : [{ qty: '', description: '', pounds: '' }]).map((line, index) => {
            const qty = Number(line.qty) || (line.pounds || line.description ? 1 : 0);
            const unit = poundsToPence(line.pounds);
            return (
              <tr key={index}>
                <td>{qty || ''}</td>
                <td className="whitespace-pre-line">{line.description}</td>
                <td className="right">{line.pounds ? formatMoney(unit, money) : ''}</td>
                <td className="right">{line.pounds ? formatMoney(Math.round(qty * unit), money) : ''}</td>
              </tr>
            );
          })}
          <tr><td>&nbsp;</td><td /><td /><td /></tr>
          <tr><td>&nbsp;</td><td /><td /><td /></tr>
          <tr>
            <td /><td />
            <td className="right font-bold">SUBTOTAL</td>
            <td className="right font-bold">{formatMoney(net, money)}</td>
          </tr>
          <tr>
            <td /><td />
            <td className={`right${vatKnown ? '' : ' font-bold'}`}>VAT</td>
            <td className={`right${vatKnown ? '' : ' font-bold'}`}>{vatKnown ? formatMoney(vat, money) : 'NONE'}</td>
          </tr>
          <tr>
            <td /><td />
            <td className="right font-bold">TOTAL</td>
            <td className="right font-bold">{formatMoney(total, money)}</td>
          </tr>
        </tbody>
      </table>
      {terms.length ? (
        <section className="mt-4 text-[9pt] leading-relaxed">
          <h2 className="mb-2 text-[11pt] font-bold">Commercial terms</h2>
          <dl className="grid grid-cols-[7.2rem_1fr] gap-x-3 gap-y-4">
            {terms.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="font-bold">{label}</dt>
                <dd><NoticeText value={value} /></dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      <div className="mt-auto px-3 py-4 text-center text-[14pt]" style={{ background: '#E7D8F5' }}>
        <p>MaxIron values your business and the relationship between our organisations.</p>
        <p className="mt-1">Your Success Drives our Business.</p>
      </div>
    </article>
  );
}
